import classNames         from 'classnames';
import * as React         from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation }    from 'react-router-dom';

import './price.style.scss';

import type { PriceProps } from './price.props';

const Price: React.FC<PriceProps> = function(props) {
  const {
          discountPercent,
          help,
          priceValidUntil,
          schema,
          showDiscountPrice,
        } = props;

  const { t }    = useTranslation();
  const location = useLocation();

  const isMarker           = React.useMemo(() => {
    return props.isExpressMarker || props.isMarker || props.isSpecMarker;
  }, [props.isExpressMarker, props.isMarker, props.isSpecMarker]);
  const discountPrice       = React.useMemo(() => {

    if (isMarker && !showDiscountPrice) {
      return;
    }

    return props.discountPrice;
  }, [isMarker, props.discountPrice, showDiscountPrice]);
  const discountPricePrefix = React.useMemo(() => {
    if (showDiscountPrice) {
      return <>{t('field_price')}:</>;
    }

    return t('label_price_from');
  }, [showDiscountPrice]);
  const price               = React.useMemo(() => {
    if (isMarker && !showDiscountPrice) {
      return props.discountPrice;
    }

    return props.price;
  }, [isMarker, props.discountPrice, props.price, showDiscountPrice]);
  const pricePrefix         = React.useMemo(() => {
    switch (true) {
      case showDiscountPrice:
        return '';
      case isMarker:
        return <>{t('field_price')}:</>;
      case !!discountPrice:
        return t('label_price_from_short')
          .toLowerCase();
      default:
        return t('label_price_from');
    }
  }, [showDiscountPrice, isMarker, discountPrice]);
  const schemaPrice         = React.useMemo(() => {
    if (!schema) {
      return {};
    }

    return {
      itemProp: 'price',
    };
  }, [schema]);
  const schemaWrapper       = React.useMemo(() => {
    if (!schema) {
      return {};
    }

    return {
      itemProp:  'offers',
      itemScope: true,
      itemType:  'http://schema.org/Offer',
    };
  }, [schema]);
  const tag                 = React.useMemo(() => {
    if (typeof discountPercent !== 'string' && typeof discountPercent !== 'number') {
      return '';
    }

    const percent = parseInt(discountPercent.toString());

    switch (true) {
      case percent <= 10:
        return 'green';

      case percent > 10 && percent <= 15:
        return 'orange';

      case percent > 15 && percent <= 20:
        return 'red';

      case percent > 20:
        return 'blue';

      default:
        return '';
    }
  }, [discountPercent]);

  const format = React.useCallback((price: number | string) => {
    return [
      new Intl.NumberFormat('ru-RU').format(+price),
      t('label_currency'),
    ].join(' ');
  }, []);

  if (!price) {
    return null;
  }

  return (
    <>
      <div
        className="price"
        {...schemaWrapper}
      >
        {!!discountPrice && (
          <span className={classNames('new')}>
            <span className={'label--from'}>
              {discountPricePrefix}{' '}
            </span>
            <span {...schemaPrice}>{format(discountPrice)}</span>
          </span>
        )}
        <span className={classNames('current_price', { 'old': !!discountPrice })}>
          <span className={'label--from'}>
            {pricePrefix}{' '}
          </span>
          <span {...schemaPrice}>{format(price)}</span>
        </span>
        {!!discountPercent && (
          <span className={classNames('tag', tag)}>
            -{parseInt(discountPercent.toString())}%
          </span>
        )}
        {schema && (
          <>
            {/* <link href>, not <meta content>: microdata resolves a relative href against the
                page URL, so the server HTML carries the real URL and the first client render
                is identical to it (window.location is unavailable on the server). */}
            <link
              href={location.pathname}
              itemProp={'url'}
            />
            <meta
              content={priceValidUntil}
              itemProp={'priceValidUntil'}
            />
            <meta
              content={price.toString()}
              property={'og:product:price:amount'}
            />
            <meta
              content={t('label_currency')}
              property={'og:product:price:currency'}
            />
          </>
        )}
      </div>
      <div className={'price-help'}>{help}</div>
    </>
  );
};

export default Price;